import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useDebouncedValue } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { SearchIcon } from '@tyro/icons';
import { useLocation } from '@tyro/router/hooks';
import { memo, useEffect, useId, useState } from 'react';
import { useOmniSearch } from '../../api/search';
import { SearchInput } from './input';
import { SearchListboxContainer } from './listbox-container';
import { SearchProvider } from './provider';
import { GroupsSection } from './sections/groups-section';
import { PagesSection } from './sections/pages-section';
import { PeopleSection } from './sections/people-section';
import { RecentAndFavoriteSearchSection } from './sections/recent-and-favourite-section';

function Searchbar() {
  const [open, setOpen] = useState(false);
  const id = useId();
  const {
    value: searchQuery,
    debouncedValue: debouncedSearchQuery,
    setValue: setSearchQuery,
  } = useDebouncedValue<string>({ defaultValue: '' });
  const location = useLocation();
  const { t } = useTranslation(['common']);

  const { data, isLoading } = useOmniSearch(debouncedSearchQuery);
  const { hasResults, pages, people, groups } = data || { hasResults: false };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && e.metaKey) {
        setOpen((currentOpenValue) => !currentOpenValue);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    if (open === true) {
      setOpen(false);
    }
  }, [location]);

  useEffect(() => {
    if (open === true) {
      setSearchQuery('');
    }
  }, [open]);

  const isExpanded = Boolean(searchQuery && !isLoading);

  return (
    <SearchProvider data={data}>
      <Box>
        <IconButton onClick={() => setOpen(true)}>
          <SearchIcon />
        </IconButton>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="sm"
          sx={{
            '& .MuiDialog-container': {
              alignItems: { xs: 'center', sm: 'flex-start' },
            },
          }}
          slotProps={{
            paper: {
              sx: {
                mx: { xs: 0, sm: 2 },
                my: { xs: 0, sm: 8 },
              },
            },
          }}
        >
          <DialogTitle component="div" sx={{ p: 0 }}>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              ariaControls={id}
              ariaExpanded={isExpanded}
            />
          </DialogTitle>
          {(!searchQuery || isLoading) && <RecentAndFavoriteSearchSection />}
          {isExpanded && (
            <>
              <Divider />
              <DialogContent dividers sx={{ px: 2 }}>
                {hasResults ? (
                  <SearchListboxContainer id={id}>
                    <PeopleSection people={people} />
                    <GroupsSection groups={groups} />
                    <PagesSection pages={pages} />
                  </SearchListboxContainer>
                ) : (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {t('common:noResultsFound')}
                  </Typography>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </SearchProvider>
  );
}

export default memo(Searchbar);
